<template>
  <div class="overflow-x-auto">
    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
      <thead class="bg-gray-50 dark:bg-gray-700">
        <tr>
          <th v-if="selectable" class="px-6 py-3 text-left">
            <input type="checkbox" :checked="isAllSelected" :indeterminate="isSomeSelected"
              class="w-4 h-4 text-indigo-600 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500" @change="handleSelectAll" />
          </th>
          <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">序号</th>
          <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">单词</th>
          <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">音标 (UK)</th>
          <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">音标 (US)</th>
          <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">翻译</th>
          <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">操作</th>
        </tr>
      </thead>
      <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
        <tr v-for="(item, index) in contents" :key="item.id" class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
          <td v-if="selectable" class="px-6 py-4">
            <input type="checkbox" :checked="selectedIds.includes(item.id)"
              class="w-4 h-4 text-indigo-600 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500"
              @change="handleSelect(item.id)" />
          </td>
          <td class="px-6 py-4 whitespace-nowraxt-sm text-gray-900 dark:text-gray-100">
            {{ showOrder ? (item.order || index + 1) : (index + 1) }}
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
            {{ getContentData(item).text }}
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
            {{ getContentData(item).ipaUk || '-' }}
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
            {{ getContentData(item).ipaUs || '-' }}
          </td>
     <td class="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
            <div class="max-w-xs truncate" :title="getContentData(item).translation">
              {{ getContentData(item).translation || '-' }}
            </div>
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
            <div class="flex items-center gap-2">
              <button class="link-btn-view" @click="$emit('edit', item)">
                编辑
              </button>
              <button class="link-btn-delete" @click="$emit('delete', item)">
                删除
              </button>
            </div>
          </td>
        </tr>
        <tr v-if="contents.length === 0">
          <td :colspan="selectable ? 7 : 6" class="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
            暂无数据
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import type { ContentSchema } from '@/types/dictation/content';
import type { ChapterContentSchema } from '@/api/dictation/chapterContent';

/**
 * 内容表格组件
 * 
 * @example
 * <ContentTable
 *   :contents="contents"
 *   :selectable="true"
 *   v-model:selected-ids="selectedIds"
 *   @edit="handleEdit"
 *   @delete="handleDelete"
 * />
 */

interface Props {
  /** 内容列表（可以是 ContentSchema 或 ChapterContentSchema） */
  contents: (ContentSchema | ChapterContentSchema)[];
  /** 是否可选择 */
  selectable?: boolean;
  /** 已选择的 ID 列表 */
  selectedIds?: number[];
  /** 是否显示排序号 */
  showOrder?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  selectable: false,
  selectedIds: () => [],
  showOrder: false,
});

const emit = defineEmits<{
  'update:selectedIds': [ids: number[]];
  'edit': [item: ContentSchema | ChapterContentSchema];
  'delete': [item: ContentSchema | ChapterContentSchema];
}>();

const isAllSelected = computed(() => {
  return props.contents.length > 0 && props.selectedIds.length === props.contents.length;
});

const isSomeSelected = computed(() => {
  return props.selectedIds.length > 0 && props.selectedIds.length < props.contents.length;
});

/**
 * 获取内容数据（兼容 ContentSchema 和 ChapterContentSchema）
 */
const getContentData = (item: ContentSchema | ChapterContentSchema): ContentSchema => {
  if ('content' in item) {
    return item.content;
  }
  return item as ContentSchema;
};

const handleSelectAll = (event: Event) => {
  const checked = (event.target as HTMLInputElement).checked;
  if (checked) {
    emit('update:selectedIds', props.contents.map(item => item.id));
  } else {
    emit('update:selectedIds', []);
  }
};

const handleSelect = (id: number) => {
  const index = props.selectedIds.indexOf(id);
  const newIds = [...props.selectedIds];
  if (index > -1) {
    newIds.splice(index, 1);
  } else {
    newIds.push(id);
  }
  emit('update:selectedIds', newIds);
};
</script>
